/**
 * Body accepted by PUT /api/v1/organizations/{org_id}/product-flags.
 *
 * Every field is required: the endpoint replaces the whole set rather than
 * patching the fields it was given, and it rejects any field it does not declare.
 * So this type is the exact list of flags this tool can write — a flag that
 * exists on the organization but is absent here cannot be saved.
 */
export interface UpdateOrgProductFlagsRequest {
  kno2_enabled: boolean
  ecr_vault_enabled: boolean
  stripe_enabled: boolean
  stripe_manual_credit_card_enabled: boolean
  single_payment_link_enabled: boolean
  bulk_payment_link_enabled: boolean
  bulk_claim_export_enabled: boolean
  office_chat_enabled: boolean
  eligibility_automation_enabled: boolean
  ai_scribe_enabled: boolean
  ai_agent_scribe_enabled: boolean
  ai_scanning_enabled: boolean
  ai_exam_history_enabled: boolean
  ai_guidelines_enabled: boolean
  ai_file_summary_enabled: boolean
  ai_voicemail_transcription_enabled: boolean
  ai_voicemail_analysis_enabled: boolean
  ai_voice_agent_enabled: boolean
  ai_call_transcription_enabled: boolean
  ai_call_analysis_enabled: boolean
  ai_inbound_sms_enabled: boolean
  capital_os_enabled: boolean
  erx_enabled: boolean
  pocketflows_enabled: boolean
  pocketflows_reviews_enabled: boolean
  pocketflows_event_triggers_enabled: boolean
  twilio_enabled: boolean
  /** Null means the organization has never had a Twilio subaccount. */
  twilio_account_sid: string | null
  twilio_voip_enabled: boolean
}

/** Every flag the update accepts, and so every flag a switch can be bound to. */
export type SettableProductFlagField = Exclude<
  keyof UpdateOrgProductFlagsRequest,
  'twilio_account_sid'
>

/** The account SID is a value rather than a switch, so it is not bindable. */
export type WritableProductFlagField = SettableProductFlagField

/**
 * How product flags are packaged for the customer.
 *
 * A grouping for this screen only — no tier is stored against an organization, and
 * selecting one here never writes anything by itself. `manual` is not a package:
 * it collects the switches Customer Support operates directly, and they stay
 * editable whichever package is selected.
 */
export type ProductFlagTier = 'manual' | 'core' | 'premium' | 'copilot'

/** Ascending, so a higher number includes everything below it. */
export const TIER_RANK = {
  manual: 0,
  core: 1,
  premium: 2,
  copilot: 3,
} as const satisfies Record<ProductFlagTier, number>

/** The packages offered as buttons. `manual` is deliberately absent. */
export const SELECTABLE_TIERS = ['core', 'premium', 'copilot'] as const satisfies readonly ProductFlagTier[]

export const TIER_LABELS = {
  manual: 'Manual',
  core: 'Core',
  premium: 'Premium',
  copilot: 'Copilot',
} as const satisfies Record<ProductFlagTier, string>

/**
 * Heading for each group of product flags on screen.
 *
 * Spelled out per tier rather than built from the labels because `manual` is not a
 * package — nothing is "included in" it — so it is named for what it is.
 */
export const TIER_SECTION_TITLES = {
  manual: 'Manual Product Flags',
  core: 'Included in Core',
  premium: 'Included in Premium',
  copilot: 'Included in Copilot',
} as const satisfies Record<ProductFlagTier, string>

export interface ProductFlagRow {
  label: string
  tier: ProductFlagTier
  field: SettableProductFlagField
  /**
   * The one product flag this one sits behind, when it sits behind one.
   *
   * A single parent rather than a list: every dependency in the catalogue is a
   * chain, so Quinn Call Summary names Quinn Call Transcription and inherits the
   * VoiP and Texting requirements through it. Readers that need the full set walk
   * the chain rather than reading a second field that could disagree with it.
   */
  requires?: SettableProductFlagField
}

const settable = (
  label: string,
  tier: ProductFlagTier,
  field: SettableProductFlagField,
  requires?: SettableProductFlagField
): ProductFlagRow => ({ label, tier, field, ...(requires !== undefined ? { requires } : {}) })

/**
 * Every product flag Customer Support manages, in display order.
 *
 * Every product flag here is one the endpoint accepts. Fields are checked against the
 * API types, so renaming one on the backend breaks the build here rather than silently
 * sending a key the endpoint rejects.
 *
 * Pocketflows and Quinn Scribe each get two rows: they are two independently
 * settable flags, and collapsing them would cost the ability to enable one alone.
 *
 * A `requires` argument is the whole dependency map for the screen: the switches, the
 * package buttons and the save confirmation all read it, so a product flag's
 * requirement is stated once here rather than three times in the code that enforces it.
 * Quinn Agent Scribe deliberately names nothing — it is independent of Quinn Scribe
 * despite the name, and gating it on Quinn Scribe would take a working product flag
 * away.
 */
export const PRODUCT_FLAG_ROWS: readonly ProductFlagRow[] = [
  // Manual — operated directly by Customer Support, outside the packages.
  settable('Payment Processing (Stripe)', 'manual', 'stripe_enabled'),
  settable(
    'Stripe Single Payment Link',
    'manual',
    'single_payment_link_enabled',
    'stripe_enabled'
  ),
  settable(
    'Stripe Bulk Payment Link',
    'manual',
    'bulk_payment_link_enabled',
    'stripe_enabled'
  ),
  settable(
    'Stripe Manual Credit Card',
    'manual',
    'stripe_manual_credit_card_enabled',
    'stripe_enabled'
  ),
  settable('ECR Vault', 'manual', 'ecr_vault_enabled'),

  // Core
  settable('Texting (Twilio)', 'core', 'twilio_enabled'),
  settable('Expense Management (Capitol OS)', 'core', 'capital_os_enabled'),
  settable('Bulk Export 837 EDI File', 'core', 'bulk_claim_export_enabled'),

  // Premium
  settable('Email Marketing (Pocketflows)', 'premium', 'pocketflows_enabled'),
  settable(
    'Reputation Management (Pocketflows)',
    'premium',
    'pocketflows_reviews_enabled',
    'pocketflows_enabled'
  ),
  settable(
    'Email Automations (Pocketflows)',
    'premium',
    'pocketflows_event_triggers_enabled',
    'pocketflows_enabled'
  ),
  settable('VoiP (Twilio)', 'premium', 'twilio_voip_enabled', 'twilio_enabled'),
  settable('eFax (Kno2)', 'premium', 'kno2_enabled'),
  settable('ePrescribe (Dosespot)', 'premium', 'erx_enabled'),
  settable('Interoffice Chat', 'premium', 'office_chat_enabled'),
  settable('Medical Eligibility (Trizetto)', 'premium', 'eligibility_automation_enabled'),

  // Copilot
  settable('Quinn Scribe', 'copilot', 'ai_scribe_enabled'),
  settable('Quinn Agent Scribe', 'copilot', 'ai_agent_scribe_enabled'),
  settable('Quinn History', 'copilot', 'ai_exam_history_enabled'),
  settable('Quinn Smart Scan', 'copilot', 'ai_scanning_enabled'),
  settable('Quinn Guidelines', 'copilot', 'ai_guidelines_enabled'),
  settable('Quinn File Summary', 'copilot', 'ai_file_summary_enabled'),
  settable(
    'Quinn Voicemail Transcription',
    'copilot',
    'ai_voicemail_transcription_enabled',
    'twilio_voip_enabled'
  ),
  settable(
    'Quinn Voicemail Summary',
    'copilot',
    'ai_voicemail_analysis_enabled',
    'ai_voicemail_transcription_enabled'
  ),
  settable(
    'Quinn Call Transcription',
    'copilot',
    'ai_call_transcription_enabled',
    'twilio_voip_enabled'
  ),
  settable(
    'Quinn Call Summary',
    'copilot',
    'ai_call_analysis_enabled',
    'ai_call_transcription_enabled'
  ),
  settable(
    'Quinn Inbound Text Media',
    'copilot',
    'ai_inbound_sms_enabled',
    'twilio_enabled'
  ),
  // Behind VoiP rather than Texting: the receptionist answers through the TwiML voice
  // app, which api-service attaches to a number only while VoiP is on.
  settable(
    'Quinn Receptionist',
    'copilot',
    'ai_voice_agent_enabled',
    'twilio_voip_enabled'
  ),
]

/**
 * The form's shape: a boolean per settable flag, plus the Twilio account SID,
 * which is a value rather than a switch.
 */
export type ProductFlagFormValues = {
  [Field in SettableProductFlagField]: boolean
} & {
  twilio_account_sid: string
}
